import { useCallback, useRef, useState } from 'react';
import {
  useArchiveForm,
  useDuplicateForm,
  useRestoreForm,
  useSaveForm,
  useSetFormStatus,
} from '../../gql';
import { brand } from '../../shared/brand';
import { formBuilderPath, formsListPath } from '../../shared/routes';
import {
  attentionMessage,
  firstFieldErrorId,
  validateForSave,
} from '../state/definitionValidation';
import { computeChanges, isDirty } from '../state/diff';
import { snapshotToSaveInput } from '../state/snapshot';

// The builder's server round trips (build plan §5.5 "Save and optimistic
// handling"): save, the Live / Draft switch, archive, restore, duplicate
// and the 409 reload. Every mutation hook resolves to { data } or
// { error: { global, fields, code } }.

export const scrollToField = (id) => {
  const el = id ? document.getElementById(`fb-field-${id}`) : null;
  if (el && el.scrollIntoView)
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const hasKeys = (object) => !!object && Object.keys(object).length > 0;

const isRevisionConflict = (error) =>
  error.code === 409 && /changed somewhere else/i.test(error.global || '');

export const statusToast = (next, savedFirst) => {
  if (next === 'live') {
    return savedFirst
      ? 'Saved and live'
      : `Form is now Live in the ${brand().name} app`;
  }
  return 'Form switched to Draft — hidden in the app';
};

const useBuilderCommands = ({
  state,
  dispatch,
  refetch,
  showToast,
  closeOverlays,
  navigate,
  routePrefix,
}) => {
  const saveForm = useSaveForm();
  const setFormStatus = useSetFormStatus();
  const archiveForm = useArchiveForm();
  const restoreForm = useRestoreForm();
  const duplicateForm = useDuplicateForm();
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // Handlers run across awaits; always read the latest state.
  const stateRef = useRef(state);
  stateRef.current = state;

  const showErrors = useCallback(
    (errorMap, message) => {
      dispatch({ type: 'SET_ERRORS', errors: errorMap });
      const first = firstFieldErrorId(
        errorMap,
        stateRef.current.working.fields
      );
      if (first) {
        dispatch({ type: 'SELECT', id: first });
        scrollToField(first);
      }
      // One problem: say what it is rather than "1 field needs…".
      const messages = Object.values(errorMap);
      showToast({
        type: 'error',
        title: messages.length === 1 ? messages[0] : message,
      });
    },
    [dispatch, showToast]
  );

  const askReload = useCallback(() => {
    closeOverlays();
    setConfirm('reload');
  }, [closeOverlays]);

  const reportError = useCallback(
    (error) => {
      if (isRevisionConflict(error)) {
        showToast({
          type: 'error',
          title: 'This form was changed somewhere else.',
          action: { text: 'Reload', onClick: askReload },
        });
      } else if (hasKeys(error.fields)) {
        showErrors(error.fields, error.global);
      } else {
        showToast({ type: 'error', title: error.global });
      }
    },
    [askReload, showErrors, showToast]
  );

  // → the saved BuilderForm, or null (nothing sent / refused).
  const runSave = useCallback(
    async ({ live = false, quiet = false } = {}) => {
      const s = stateRef.current;
      const errors = validateForSave(s.working, {
        live: live || s.status === 'live',
        lockedFieldIds: s.lockedFieldIds,
      });
      if (hasKeys(errors)) {
        showErrors(errors, attentionMessage(errors));
        return null;
      }
      const sent = s.working;
      setSaving(true);
      const { data, error } = await saveForm(
        snapshotToSaveInput(sent, s.formId, s.revision)
      );
      setSaving(false);
      if (error) {
        reportError(error);
        return null;
      }
      dispatch({ type: 'SAVED', form: data, sent });
      if (!quiet)
        showToast(
          s.status === 'live' ? 'Saved. The live form is updated' : 'Form saved'
        );
      return data;
    },
    [dispatch, reportError, saveForm, showErrors, showToast]
  );

  // One save at a time. A second call while one is in flight (Save, then
  // the Live switch or "Save and leave") gets the pending promise instead
  // of sending the same revision again, which the server would refuse
  // with a 409.
  const inFlight = useRef(null);
  const save = useCallback(
    (options) => {
      if (inFlight.current) return inFlight.current;
      const pending = runSave(options).finally(() => {
        inFlight.current = null;
      });
      inFlight.current = pending;
      return pending;
    },
    [runSave]
  );

  const discard = useCallback(
    ({ quiet = false } = {}) => {
      dispatch({ type: 'DISCARD' });
      if (!quiet) showToast('Changes discarded');
    },
    [dispatch, showToast]
  );

  const changeStatus = useCallback(
    async (next, dirty) => {
      const s = stateRef.current;
      const savedFirst = next === 'live' && dirty;
      if (savedFirst && !(await save({ live: true, quiet: true }))) return;
      const sent = stateRef.current.working;
      const { data, error } = await setFormStatus({
        id: s.formId,
        status: next,
      });
      if (error) {
        reportError(error);
        return;
      }
      // Going Draft with unsaved edits changes the status only.
      if (next === 'draft' && dirty) {
        dispatch({
          type: 'SET_STATUS',
          status: data.status,
          archived: !!data.archived_at,
        });
      } else {
        dispatch({ type: 'SAVED', form: data, sent });
      }
      showToast(statusToast(next, savedFirst));
    },
    [dispatch, reportError, save, setFormStatus, showToast]
  );

  const setStatus = useCallback(
    async (next) => {
      const s = stateRef.current;
      const dirty = isDirty(computeChanges(s.saved, s.working));
      if (next === 'live') {
        const errors = validateForSave(s.working, {
          live: true,
          lockedFieldIds: s.lockedFieldIds,
        });
        if (hasKeys(errors)) {
          showErrors(errors, attentionMessage(errors));
          return;
        }
      }
      setStatusBusy(true);
      try {
        await changeStatus(next, dirty);
      } finally {
        setStatusBusy(false);
      }
    },
    [changeStatus, showErrors]
  );

  const restore = useCallback(async () => {
    setRestoring(true);
    const { data, error } = await restoreForm(stateRef.current.formId);
    setRestoring(false);
    if (error) {
      showToast({ type: 'error', title: error.global });
      return;
    }
    dispatch({
      type: 'SET_STATUS',
      status: data.status,
      archived: !!data.archived_at,
    });
    showToast(`“${data.name}” restored`);
  }, [dispatch, restoreForm, showToast]);

  const requestArchive = useCallback(() => {
    closeOverlays();
    setConfirm('archive');
  }, [closeOverlays]);

  const archive = useCallback(async () => {
    setConfirm(null);
    const s = stateRef.current;
    const { error } = await archiveForm(s.formId);
    if (error) {
      showToast({ type: 'error', title: error.global });
      return;
    }
    navigate(formsListPath(routePrefix), {
      toast: `“${s.saved.name}” archived`,
    });
  }, [archiveForm, navigate, routePrefix, showToast]);

  const duplicate = useCallback(async () => {
    const { data, error } = await duplicateForm({
      id: stateRef.current.formId,
    });
    if (error) {
      showToast({ type: 'error', title: error.global });
      return;
    }
    navigate(formBuilderPath(routePrefix, data.id), {
      toast: `Duplicated as “${data.name}”`,
    });
  }, [duplicateForm, navigate, routePrefix, showToast]);

  const reload = useCallback(async () => {
    setConfirm(null);
    try {
      const res = await refetch();
      const fresh = res && res.data && res.data.getFormAdmin;
      if (!fresh) throw new Error('empty');
      dispatch({ type: 'INIT', form: fresh });
      showToast('Loaded the latest version');
    } catch (e) {
      showToast({
        type: 'error',
        title: "Couldn't reload the form. Try again.",
      });
    }
  }, [dispatch, refetch, showToast]);

  return {
    save,
    discard,
    setStatus,
    restore,
    requestArchive,
    archive,
    duplicate,
    askReload,
    reload,
    confirm,
    cancelConfirm: () => setConfirm(null),
    saving,
    statusBusy,
    restoring,
  };
};

export default useBuilderCommands;
